import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PaginatedResponse } from '@supernova/shared';

export function usePurchaseOrders(params: Record<string, string | number | boolean | undefined> = {}) {
  return useQuery({
    queryKey: ['purchase-orders', params],
    queryFn: () => api.get<PaginatedResponse<any>>('/purchase-orders', params),
  });
}

export function usePurchaseOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['purchase-orders', id],
    queryFn: () => api.get<{ data: any }>(`/purchase-orders/${id}`),
    enabled: !!id,
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/purchase-orders', data),
    // The PO form catches and surfaces VALIDATION_ERROR/Missing-units inline
    // (red banner + inline edit affordances). Letting the global toast fire
    // creates a duplicate, dismissable noise that confused users into thinking
    // their fix didn't apply — see dev feedback on PR #254.
    meta: { suppressGlobalError: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/purchase-orders/${id}`, data),
    meta: { suppressGlobalError: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useReceivePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/purchase-orders/${id}/receive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

// Correct the unit conversion (baseQty) on a PO line. For received POs the
// server also recomputes and applies the stock delta atomically.
export function useCorrectConversion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      poId,
      lineId,
      newBaseQty,
      alsoUpdateProductUnit,
    }: {
      poId: string;
      lineId: string;
      newBaseQty: number;
      alsoUpdateProductUnit?: boolean;
    }) =>
      api.post<{
        data: {
          itemId: string;
          oldBaseQty: number;
          newBaseQty: number;
          stockDelta: number;
          stockApplied: boolean;
          productUnitUpdated: boolean;
        };
      }>(`/purchase-orders/${poId}/items/${lineId}/correct-conversion`, {
        newBaseQty,
        alsoUpdateProductUnit: alsoUpdateProductUnit ?? true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useResendPurchaseOrderEmail() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { sent: boolean; to: string } }>(`/purchase-orders/${id}/resend-email`),
  });
}

// Send a draft PO to the vendor (transition draft → sent + dispatch email).
export function useSendPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: any; sent?: { to: string } }>(`/purchase-orders/${id}/send`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

// Cancel a PO. For sent POs, the vendor receives a cancellation notice.
export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<{ data: any; cancelledFromStatus: string }>(`/purchase-orders/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

// Email send/resend/cancel audit trail for a PO. Powers the email-history
// panel on the PO detail page.
export function usePurchaseOrderEmailLog(id: string | undefined) {
  return useQuery({
    queryKey: ['purchase-orders', id, 'email-log'],
    queryFn: () => api.get<{ data: any[] }>(`/purchase-orders/${id}/email-log`),
    enabled: !!id,
  });
}

// Recent POs for a specific vendor — used by the Send Payments app to surface
// purchasing activity alongside payments without cross-service syncing.
export function useVendorPurchaseOrders(payeeId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ['vendor-purchase-orders', payeeId, limit],
    queryFn: () =>
      api.get<{
        data: any[];
        summary: {
          total: number;
          drafts: number;
          sent: number;
          received: number;
          cancelled: number;
          outstanding: string;
        };
      }>(`/payees/${payeeId}/purchase-orders`, { limit }),
    enabled: !!payeeId,
  });
}
